<script setup lang="ts">
import type { ProductsList } from "~/models/Content/ContentProducts"
import { formProductKeys } from "~/components/Hubspot/productForms"
import { keys } from "~/utilities/injection"

export type ViewType = "list" | "detail"

const { product } = defineProps<{
  product: ProductsList
  viewType: ViewType
  align: "center" | "left"
  showStatusDetails: boolean
}>()

const showVariantModal = ref(false)
provide(keys.showVariantModal, showVariantModal)

const linkProps = computed(() => {
  if (product.variants.length === 1) {
    const path = `/produkter/${product.productKey ?? "exphil"}/${
      product.variants[0]?.variantKey
    }`

    return {
      to: {
        path,
        query: !hasValidLicence.value
          ? { "til-gratis-kapittel": String(true) }
          : undefined,
      },
    }
  } else {
    return {
      onClick: () => {
        showVariantModal.value = true
      },
    }
  }
})

const {
  data,
  isLoading: isEcommerceCatalogueLoading,
  error: isEcommerceCatalogueError,
} = useEcommerceCatalogue()

const toastStore = useToastStore()
const ecommerceCatalogueErrorToastId = "ecommerce-catalogue-error"

watch(
  () => isEcommerceCatalogueError,
  newValue => {
    if (newValue) {
      toastStore.addToast({
        id: ecommerceCatalogueErrorToastId,
        message:
          "Vi klarte ikke å hente prisinformasjonen for produktet",
        type: "error",
      })
    }
  },
)

// Find first product that has an EAN _and_ a set price in Crystallize, as all variants should have same price.
const productVariantPrice = computed(() => {
  const prices = product.variants
    .filter(variant => !!variant.ean)
    .map(v => getProductPrice(v, data))

  return prices.find(it => typeof it?.actual === "number")
})

const hasInterestForm = computed(() => !!formProductKeys.find(it => it === product.productKey))

const hasValidLicence = computed(() => product.variants.some(p => p.userHasLicence))

const productHasFreeVariant = computed(() => product.variants.some(it => getProductPrice(it.ean, data)?.actual === 0))
</script>

<template>
  <div>
    <UChooseInstitute
      :product="product"
      :show-variant-modal="showVariantModal"
    />
    <div
      class="flex flex-wrap xl:flex-nowrap gap-x-4 lg:justify-between"
      :class="{
        'flex-col lg:items-start gap-y-6 lg:gap-y-8 items-center':
          viewType === 'detail',
        'gap-y-4 flex-col lg:flex-row items-start':
          viewType === 'list',
        'flex-col lg:items-center justify-center': align === 'center',
      }"
    >
      <USimpleSkeleton v-if="isEcommerceCatalogueLoading" />
      <div
        v-else-if="
          productVariantPrice &&
          product.hasAtLeastOneReleasedProduct &&
          !isEcommerceCatalogueLoading
        "
        class="flex flex-col gap-2 text-3xl font-extrabold text-left grow"
        :class="{
          'gap-y-1 lg:gap-y-2 items-start': viewType === 'list',
          'gap-y-2 lg:gap-y-4 text-marketing-mobile-p1-p1-bold-l lg:text-marketing-desktop-p1-p1-bold-l items-center lg:items-start':
            viewType === 'detail',
          'lg:items-center': align === 'center',
        }"
      >
        <UPrice
          :price="productVariantPrice"
          :view-type="viewType"
        />
        <UProductDiscountedPrice
          :price="productVariantPrice"
          :product="product"
          :show-status-details="showStatusDetails"
          class="font-normal text-[--theme-darker]"
          :class="{
            'text-lg lg:text-xl font-national2': viewType === 'list',
            'text-2xl lg:text-3xl font-national2condensed text-center lg:text-left': viewType === 'detail',
          }"
        />
      </div>
      <div
        v-else-if="
          viewType === 'list' && !isEcommerceCatalogueLoading
        "
        class="flex grow items-center italic text-[--theme-dark]"
      >
        <p
          class="font-extrabold text-marketing-mobile-p1-p1-bold-m lg:text-marketing-desktop-p1-p1-bold-s font-national2condensed"
        >
          Kommer snart
        </p>
      </div>
      <div
        class="flex flex-wrap gap-4 md:flex-nowrap"
        :class="{
          'lg:self-end': viewType === 'list',
          'justify-center': viewType === 'detail',
        }"
      >
        <UButtonNew
          v-if="viewType === 'list'"
          bold
          size="md"
          :to="`/produkter/${product.urlFormatProductTitle}`"
        >
          Les mer

          <span class="sr-only"
            >om Kunne {{ product.productTitle }}</span
          >
        </UButtonNew>
        <UButtonNew
          v-if="
            product.hasAtLeastOneReleasedProduct &&
            (hasValidLicence ||
              viewType === 'detail' ||
              productHasFreeVariant)
          "
          bold
          :pill="viewType !== 'detail'"
          :theme="viewType !== 'detail' ? 'cta' : undefined"
          size="md"
          :variant="viewType !== 'detail' ? 'filled' : undefined"
          v-bind="linkProps"
        >
          {{ !hasValidLicence ? "Prøv gratis" : "Til produktet" }}

          <span class="sr-only"
            >om Kunne {{ product.productTitle }}</span
          >
        </UButtonNew>

        <UButtonNew
          v-if="
            product.hasAtLeastOneReleasedProduct &&
            !productHasFreeVariant &&
            !hasValidLicence
          "
          variant="filled"
          theme="cta"
          pill
          size="md"
          data-pendo="forside-kjop-tilgang"
          bold
          :to="`/produkter/${product.urlFormatProductTitle}/betaling`"
        >
          Kjøp
          <span class="hidden sm:inline">tilgang</span>
          <span class="sr-only"
            >til Kunne {{ product.productTitle }}</span
          >
        </UButtonNew>
        <UButtonNew
          v-else-if="!product.hasAtLeastOneReleasedProduct && hasInterestForm"
          :to="`/produkter/${product.urlFormatProductTitle}/registrer-interesse`"
          :data-pendo="`forside-hold-meg-oppdatert-${product.urlFormatProductTitle}`"
          variant="filled"
          theme="cta"
          size="md"
          pill
        >
          Hold meg oppdatert
        </UButtonNew>
      </div>
    </div>
  </div>
</template>
